import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Document from './components/Document'
import { AttorneyBlock, IconButton } from '../../../ui'
import { Export } from '../../../../assets/icons'
import { Upload, message, Alert, Divider, Checkbox } from 'antd'
import SignUtils from '../../../../utils/sign/SignUtils'
import type { UploadProps } from 'antd/es/upload/interface'
import request from '../../../../services/request'
import useRequest, { RequestTypes } from '../../../hooks/useRequest'
import {errorHandler, checkExtensionXML, isExpired} from '../../../helpers'

import style from './style.m.less'
import { IAttorneyData } from '../../../../store/extension'

interface IDocumentCheckBlock {
    fileList: { list: any[]; total: number }
    updateFileList: (
        file: any,
        id: string,
        hash: string,
        comment: string,
        document_type: number | null,
    ) => void
    deleteFromFileList: (uid: string) => void
    className?: string
    withoutComment?: boolean
    maxFileSize?: number
    blockTitle?: string
    documentType?: number | null
    prevAttorney: IAttorneyData | null
    updateAttorney: (attorney: IAttorneyData | null) => void
    showDeleteBtn?: boolean
    setInvalidAttorney: (invalidAttorney: boolean) => void
    withoutAttorney: boolean
    onWithoutAttorneyChange: (checked: boolean) => void,
    getFirstName: () => string,
    getLastName: () => string,
    getMiddleName: () => string,
    getCompanyInn: () => string
}

const DocumentCheckBlock: FC<IDocumentCheckBlock> = ({
    fileList,
    updateFileList,
    deleteFromFileList,
    className = '',
    withoutComment = false,
    maxFileSize,
    blockTitle,
    documentType = null,
    prevAttorney,
    updateAttorney,
    showDeleteBtn = true,
    setInvalidAttorney,
    withoutAttorney,
    onWithoutAttorneyChange,
    getFirstName,
    getLastName,
    getMiddleName,
    getCompanyInn
}) => {
    const { t } = useTranslation()
    const { list, total } = fileList
    const [fileError, setFileError] = useState<string[] | null>(null)
    const [documentData, setDocumentData] = useState<IAttorneyData | null>(null)

    const SIZE_ERR_TEXT = 'Суммарный объем приложенных файлов к анкете не должен превышать 50 МБ'
    const SIZE_ONE_ERR_TEXT = `Размер загружаемого файла не должен превышать ${maxFileSize} МБ`
    const MAX_SIZE = 50000000

    const {
        fetch: uploadFile,
        isLoading: loadingFile,
        result: resFile,
    } = useRequest({
        link: '/api/extension/v1/files/mchd/validations/',
        method: RequestTypes.post,
    })

    useEffect(() => {
        if (prevAttorney) {
            setDocumentData(prevAttorney)
            updateAttorney(prevAttorney)
            if (isExpired(prevAttorney.validity_period, true)) {
                setInvalidAttorney(true)
            }
        } else if (!withoutAttorney) {
            setInvalidAttorney(true)
        }
    }, [prevAttorney])

    const handleDeleteFromFileList = (uid: string): void => {
        setDocumentData(null)
        updateAttorney(null)
        return deleteFromFileList(uid)
    }

    const handleWithoutAttorneyChange = (without_attorney: boolean): void => {
        if (!!list.length) {
            list.forEach((item) => {
                handleDeleteFromFileList(item.id || item.uid)
            })
        }

        if (without_attorney) {
            setFileError(null)
            setInvalidAttorney(false)
        } else if (!documentData || !list.length) {
            setInvalidAttorney(true)
        }

        onWithoutAttorneyChange(without_attorney)
    }

    const uploadProps: UploadProps = {
        showUploadList: false,

        customRequest: async (options) => {
            function readFileAsync(file: any) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader()

                    reader.onload = () => {
                        resolve(reader.result)
                    }

                    reader.onerror = reject

                    reader.readAsArrayBuffer(file)
                })
            }

            setFileError(null)
            setInvalidAttorney(false)
            const { file } = options
            const SIZES_SUMMARY = total + (file as File).size

            if (maxFileSize && maxFileSize * 1000000 < (file as File).size) {
                message.error(SIZE_ONE_ERR_TEXT)
                return
            }

            if (SIZES_SUMMARY > MAX_SIZE) {
                message.error(SIZE_ERR_TEXT)
            } else if (!checkExtensionXML((file as File).name)) {
                message.error(errorHandler('ERR_APL_51'))
            } else {
                setDocumentData(null)
                const formData = new FormData()
                formData.append('file', file as File)
                formData.append('first_name', getFirstName())
                formData.append('last_name', getLastName())
                formData.append('middle_name', getMiddleName())
                formData.append('company_inn', getCompanyInn())

                uploadFile({
                    body: formData,
                    showError: false,
                    onSuccess: async (data: IAttorneyData) => {
                        const result = await readFileAsync(file as File)
                        const res = await request.post('/api/documents/v1/file', result, {
                            headers: {
                                Filename: encodeURI((file as File).name),
                                'Service-ID': 2,
                            },
                        })
                        if (res.status === 200) {
                            // Удаляем предыдущие файлы МЧД
                            if (!!list.length) {
                                list.forEach((item) => {
                                    handleDeleteFromFileList(item.id || item.uid)
                                })
                            }
                            const id = res.data.id
                            const hash = await SignUtils.hashBytes(result)
                            updateFileList(file, id, hash, '', documentType)
                            setDocumentData(data)
                            updateAttorney(data)
                        }
                    },
                    onError: (err) => {
                        if (err?.detail?.length > 0) {
                            setFileError(err.detail)
                            setInvalidAttorney(true)
                        }
                    },
                })
            }
        },
    }

    return (
        <>
            {blockTitle && <div className={style.blockHeader}>{blockTitle}</div>}
            <div className={className || style.documentsBlock}>
                {!!list.length && !fileError && !!documentData &&
                    list?.map((item) => (
                        <Document
                            key={item.id}
                            file={item}
                            deleteFromFileList={handleDeleteFromFileList}
                            withoutComment={withoutComment}
                            showDeleteBtn={showDeleteBtn}
                        />
                    ))
                }
                {!!documentData && !!list.length && <AttorneyBlock attorney={documentData} />}
                {!withoutAttorney && !documentData && !list.length && !fileError && <Alert
                    className={style.fileError}
                    message={t('profile.attorneyIsMissing')}
                    type="error"
                    showIcon
                />}
                {!!fileError &&
                    fileError?.map((msg: string) => (
                        <Alert type="error" showIcon className={style.fileError} message={msg}/>
                    ))
                }
                <div className={style.uploadDocumentFrame}>
                    <Upload {...uploadProps}>
                        <IconButton
                            icon={<Export />}
                            disabled={loadingFile || withoutAttorney}
                            loading={loadingFile}
                            customIconStyle={{ marginRight: '8px', marginTop: '3px' }}
                        >
                            {t('editMyProfile.uploadDoc')}
                        </IconButton>
                    </Upload>
                    <div className={style.info}>{t('editMyProfile.uploadNecessaryDocsXML')}</div>
                </div>
            </div>
            <div className={style.checkboxBlock}>
                <Checkbox
                    // disabled={!!list.length && !!documentData}
                    checked={withoutAttorney}
                    onChange={
                        (e) => handleWithoutAttorneyChange(e.target.checked) // onWithoutAttorneyChange(e.target.checked)
                    }
                >
                    {t('profile.withoutAttorney')}
                </Checkbox>
            </div>
            <Divider />
        </>
    )
}

export default DocumentCheckBlock
